/**
 * Internationalization (i18n) System
 * Handles language switching between Spanish and English
 */

import { translations } from '../data/translations';

// Export Language type
export type Language = 'es' | 'en';

// Export Translation interface
export interface Translation {
  [key: string]: string | Translation;
}

export class I18n {
  private currentLanguage: Language;
  private translations: Record<Language, Translation>;

  constructor() {
    // Load language preference from localStorage or default to Spanish
    const savedLanguage = localStorage.getItem('besafe-language') as Language;
    this.currentLanguage = savedLanguage || 'es';
    this.translations = translations;
  }

  /**
   * Get the current language
   */
  getCurrentLanguage(): Language {
    return this.currentLanguage;
  }

  /**
   * Set the language and update all content
   */
  setLanguage(lang: Language): void {
    if (lang !== 'es' && lang !== 'en') {
      console.warn(`Invalid language: ${lang}. Defaulting to 'es'`);
      lang = 'es';
    }

    this.currentLanguage = lang;
    
    // Save preference to localStorage
    localStorage.setItem('besafe-language', lang);
    
    // Update all page content
    this.updatePageContent();
  }

  /**
   * Get translation for a key using dot notation
   * Example: t('hero.title') returns the hero title in current language
   */
  t(key: string): string {
    const keys = key.split('.');
    let value: any = this.translations[this.currentLanguage];

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        console.warn(`Translation key not found: ${key}`);
        return key; // Return the key itself if translation not found
      }
    }

    if (typeof value === 'string') {
      return value;
    } else {
      console.warn(`Translation key does not resolve to a string: ${key}`);
      return key;
    }
  }

  /**
   * Update all elements with data-i18n attribute
   */
  updatePageContent(): void {
    const elements = document.querySelectorAll('[data-i18n]');
    
    elements.forEach((element) => {
      const key = element.getAttribute('data-i18n');
      if (key) {
        const translation = this.t(key);
        element.textContent = translation;
      }
    });

    // Dispatch custom event to notify components of language change
    window.dispatchEvent(new CustomEvent('languageChanged', { 
      detail: { language: this.currentLanguage } 
    }));
  }

  /**
   * Get all available languages
   */
  getAvailableLanguages(): Language[] {
    return ['es', 'en'];
  }
}

// Create and export a singleton instance
export const i18n = new I18n();
